import { useEffect, useState } from 'react';

import { DEMO_REQUESTS_CHANGED_EVENT, crmGql } from '../../lib/crmApi';
import { DEMO_NEW_COUNT_QUERY } from './queries';
import { DEMO_INTENT } from '../../constants/leadPipeline';

/** How often the count is refreshed while the tab is visible (§2 D8). */
export const NEW_COUNT_INTERVAL_MS = 60_000;

/**
 * How many demo requests nobody has answered yet — the nav badge and the
 * page's header count (§2 D8: polling, not push, in v1).
 *
 * One `crmLeads(intent, statuses:[new], limit:1).total`, refreshed on mount,
 * on window focus, when the tab becomes visible again, after any write on the
 * screen (DEMO_REQUESTS_CHANGED_EVENT), and every minute while the document is
 * visible — a hidden tab does not poll. A failure answers null, which HIDES
 * the badge: a stale or invented number beside «درخواست‌های دمو» would be a
 * claim about the queue nobody can check.
 */
export default function useNewDemoCount({
  enabled = true, intervalMs = NEW_COUNT_INTERVAL_MS, intent = DEMO_INTENT,
} = {}) {
  const [count, setCount] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setCount(null);
      return undefined;
    }
    let live = true;
    let seq = 0;
    const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

    const load = () => {
      if (hidden()) return;
      seq += 1;
      const mine = seq;
      crmGql(DEMO_NEW_COUNT_QUERY, { intent })
        .then((d) => {
          if (!live || mine !== seq) return;
          const total = Number(d?.crmLeads?.total);
          setCount(Number.isFinite(total) ? total : null);
        })
        .catch(() => { if (live && mine === seq) setCount(null); });
    };

    load();
    const timer = setInterval(load, intervalMs);
    const onVisible = () => { if (!hidden()) load(); };
    window.addEventListener('focus', load);
    window.addEventListener(DEMO_REQUESTS_CHANGED_EVENT, load);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      live = false;
      clearInterval(timer);
      window.removeEventListener('focus', load);
      window.removeEventListener(DEMO_REQUESTS_CHANGED_EVENT, load);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, intervalMs, intent]);

  return count;
}
